import { Chip, Stack, Tooltip, Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCalendarCheck, faCalendarDay } from "@fortawesome/pro-solid-svg-icons"

import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useDateTime } from "../../../../../../modules/localization/useDateTime"
import { StandardButton } from "../../../../buttons/StandardButton"

import { useFilterChipStyles } from "./filterChipStyles"
import type { MeetingWindow } from "./useMeetingAvailability"

const useStyles = makeStyles()((theme) => ({
    banner: {
        flexWrap: "wrap",
        width: "fit-content",
        maxWidth: "100%",
        minWidth: 0,
        padding: ".375rem .5rem .375rem .875rem",
        backgroundColor: theme.palette.tailwind.amber[50],
        border: `1px solid ${theme.palette.tailwind.amber[300]}`,
        borderRadius: ".75rem",
    },
    shrinkable: {
        flex: "0 1 auto",
        minWidth: 0,
    },
    label: {
        lineHeight: 1.3,
        whiteSpace: "nowrap",
        color: theme.palette.tailwind.slate[500],
    },
    summary: {
        fontWeight: 600,
        lineHeight: 1.3,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        color: theme.palette.tailwind.slate[900],
    },
    goToTimeButton: {
        flex: "none",
        whiteSpace: "nowrap",
        fontSize: ".875rem",
        color: theme.palette.tailwind.amber[800],
    },
    tooltipAnchor: {
        display: "inline-flex",
        flex: "none",
    },
    fitChip: {
        flex: "none",
        // MUI pulls the icon into the label with a negative margin; the label's own padding is the gap.
        "& .MuiChip-icon": {
            marginLeft: 0,
            marginRight: 0,
        },
    },
    chipIcon: {
        fontSize: "1rem",
        color: theme.palette.tailwind.slate[400],
    },
    chipIconActive: {
        color: theme.palette.common.white,
    },
    chipCount: {
        marginLeft: ".5rem",
        fontWeight: 600,
        color: theme.palette.tailwind.slate[500],
    },
    chipCountActive: {
        color: theme.palette.tailwind.slate[300],
    },
}))

type MeetingFitBannerProps = Readonly<{
    meetingWindow: MeetingWindow | null
    guestCount: number
    fitsCount: number
    allCount: number
    isOn: boolean
    onToggle: (next: boolean) => void
    /** Omitted when Outlook sent no meeting time to go to. */
    onGoToTime?: () => void
}>

export function MeetingFitBanner({
    meetingWindow,
    guestCount,
    fitsCount,
    allCount,
    isOn,
    onToggle,
    onGoToTime,
}: MeetingFitBannerProps) {
    const { classes, cx } = useStyles()
    const { classes: chipClasses } = useFilterChipStyles()
    const dateTimeFactory = useDateTime()

    const renderLabel = () =>
        meetingWindow ? (
            <LocalizedStrict id="create-order-timeline-meeting-fit-banner-label">
                <Typography variant="caption" className={classes.label}>
                    Meeting time
                </Typography>
            </LocalizedStrict>
        ) : (
            <LocalizedStrict id="create-order-timeline-meeting-fit-banner-label-attendees-only">
                <Typography variant="caption" className={classes.label}>
                    Attendees
                </Typography>
            </LocalizedStrict>
        )

    const renderSummary = () => {
        if (!meetingWindow) {
            // the label above already says "Attendees", so the value is the bare count
            return (
                <Typography variant="body2" className={classes.summary}>
                    {guestCount}
                </Typography>
            )
        }

        const start = dateTimeFactory.fromISO(meetingWindow.start)
        const vars = {
            start: dateTimeFactory.formatTime(start),
            end: dateTimeFactory.formatTime(dateTimeFactory.fromISO(meetingWindow.end)),
            date: dateTimeFactory.formatCustom(start, "d LLL"),
        }
        const timeRange = `${vars.start}-${vars.end}`

        return guestCount > 0 ? (
            <LocalizedStrict
                id="create-order-timeline-meeting-fit-banner-summary"
                vars={{ ...vars, count: guestCount }}
            >
                <Typography variant="body2" className={classes.summary}>
                    {timeRange}
                </Typography>
            </LocalizedStrict>
        ) : (
            <LocalizedStrict id="create-order-timeline-meeting-fit-banner-summary-no-attendees" vars={vars}>
                <Typography variant="body2" className={classes.summary}>
                    {timeRange}
                </Typography>
            </LocalizedStrict>
        )
    }

    // Off, the count is a preview of what turning it on would leave, so it needs the total to compare against.
    const renderFitCount = () =>
        isOn ? (
            <Typography component="span" variant="body2" className={cx(classes.chipCount, classes.chipCountActive)}>
                {fitsCount}
            </Typography>
        ) : (
            <LocalizedStrict
                id="create-order-timeline-meeting-fit-banner-fits-chip-count"
                vars={{ fits: fitsCount, all: allCount }}
            >
                <Typography component="span" variant="body2" className={classes.chipCount}>
                    {fitsCount} of {allCount}
                </Typography>
            </LocalizedStrict>
        )

    // Seats narrowing needs attendees, availability narrowing needs a known time — promise only what applies.
    const enableTooltipId = !meetingWindow
        ? "create-order-timeline-meeting-fit-banner-fits-chip-tooltip-enable-seats"
        : guestCount > 0
          ? "create-order-timeline-meeting-fit-banner-fits-chip-tooltip-enable-seats-and-availability"
          : "create-order-timeline-meeting-fit-banner-fits-chip-tooltip-enable-availability"

    return (
        <Stack direction="row" alignItems="center" columnGap="1rem" rowGap=".25rem" className={classes.banner}>
            <Stack direction="row" alignItems="center" gap="1.25rem" className={classes.shrinkable}>
                <Stack className={classes.shrinkable}>
                    {renderLabel()}
                    {renderSummary()}
                </Stack>

                {onGoToTime && (
                    <LocalizedStrict
                        id="create-order-timeline-meeting-fit-banner-go-to-time-button-tooltip"
                        attrs={{ title: true }}
                    >
                        <Tooltip title="Goes to the meeting date and time">
                            {/* LocalizedStrict drops the ref Tooltip needs */}
                            <span className={classes.tooltipAnchor}>
                                <LocalizedStrict id="create-order-timeline-meeting-fit-banner-go-to-time-button">
                                    <StandardButton
                                        color="grey"
                                        fullWidth={false}
                                        variant="text"
                                        size="small"
                                        updateDefaultClasses={classes.goToTimeButton}
                                        startIcon={<FontAwesomeIcon icon={faCalendarDay} />}
                                        onClick={onGoToTime}
                                    >
                                        Go to the time
                                    </StandardButton>
                                </LocalizedStrict>
                            </span>
                        </Tooltip>
                    </LocalizedStrict>
                )}
            </Stack>

            <LocalizedStrict
                id={isOn ? "create-order-timeline-meeting-fit-banner-fits-chip-tooltip-disable" : enableTooltipId}
                attrs={{ title: true }}
            >
                <Tooltip title="">
                    <Chip
                        icon={
                            <FontAwesomeIcon
                                icon={faCalendarCheck}
                                className={cx(classes.chipIcon, isOn && classes.chipIconActive)}
                            />
                        }
                        label={
                            <>
                                <LocalizedStrict id="create-order-timeline-meeting-fit-banner-fits-chip">
                                    <>Fits the meeting</>
                                </LocalizedStrict>
                                {renderFitCount()}
                            </>
                        }
                        aria-pressed={isOn}
                        onClick={() => onToggle(!isOn)}
                        className={cx(
                            chipClasses.chip,
                            classes.fitChip,
                            isOn ? chipClasses.selectedChip : chipClasses.unselectedChip,
                        )}
                    />
                </Tooltip>
            </LocalizedStrict>
        </Stack>
    )
}
